import { native } from '@forest/native-services'
import { createMutation, useQueryClient } from '@tanstack/solid-query'
import { isHTTPError } from 'ky'
import type { BoostingPack } from '~/config'
import { toCurrencyRewards } from '~/lib/currency-reward'
import { openIntercom } from '~/lib/intercom'
import { isConnectivityError } from '~/lib/is-connectivity-error'
import { platform } from '~/lib/params'
import { m } from '~/paraglide/messages'
import { api, getCurrentUserId } from '~/services/api'
import { openBoostingPackReward } from '~/store/boosting-pack-reward'
import { closeBoostingPacks } from '~/store/boosting-packs'
import { closeDialog, showDialog } from '~/store/dialog'
import {
  AfterPaymentError,
  NativeStoreError,
  purchaseBoostingPack,
} from './boosting-pack-purchase'
import { markBoostingPackPurchased } from './boosting-pack-stats'

/**
 * Solid-query hook that runs {@link purchaseBoostingPack} with the real native
 * bridge + API, optimistically marks the pack purchased on success, and maps
 * failures onto the shared dialog/Intercom flow (mirroring focus-pass).
 */
export const useBoostingPackPurchase = () => {
  const queryClient = useQueryClient()
  return createMutation(() => ({
    mutationFn: async (pack: BoostingPack) => {
      const userId = await getCurrentUserId()
      await purchaseBoostingPack({
        api,
        platform,
        userId,
        product: { product_id: pack.productId, sku: pack.sku },
        nativePurchase: native.iap.purchase,
        closeTransaction: native.iap.closeTransaction,
        onStepError: (error, params) => {
          console.error('Boosting pack purchase step failed', params, error)
        },
      })
      return pack
    },
    onSuccess: (pack) => {
      // The receipt system grants coins/gems/cookies — and finalizes the
      // purchase — async server-side. We deliberately do NOT invalidate any
      // queries here: a refetch would pull pre-grant values and clobber both
      // the count-up animations (balances) and the Limit-1 lock (pack stats),
      // since `own-purchase-stats` still reports 0 until the receipt settles.
      // The optimistic update below holds the lock; with staleTime: Infinity it
      // survives the session, and the real value lands on the next cold load.
      // The reward queue mirrors each currency into the cache as it's claimed.
      markBoostingPackPurchased(queryClient, pack.productId)
      closeBoostingPacks()
      openBoostingPackReward(toCurrencyRewards(pack.rewards))
    },
    onError: (error) => {
      if (error instanceof NativeStoreError) {
        // User cancelled the native payment sheet — stay silent.
        if (error.message === 'interrupted') return
        showDialog({
          title: m.dialog_payment_timeout_title(),
          description: m.dialog_payment_timeout_content({
            error: error.message,
          }),
          primaryButtonText: m.ok(),
          primaryAction: () => closeDialog(),
        })
        return
      }

      if (error instanceof AfterPaymentError) {
        showDialog({
          title: m.dialog_delivery_issue_title(),
          description: m.dialog_delivery_issue_description({
            orderid: `${error.purchaseId}\nError step: ${error.node}`,
          }),
          primaryButtonText: m.contact_cs(),
          primaryAction: () => {
            closeDialog()
            openIntercom({
              orderId: error.purchaseId.toString(),
              transactionId: error.transactionId ?? '',
              node: error.node,
            })
          },
        })
        return
      }

      if (isConnectivityError(error)) {
        showDialog({
          title: m.dialog_connection_issue_title(),
          description: m.dialog_connection_issue_description(),
          primaryButtonText: m.ok(),
          primaryAction: () => closeDialog(),
        })
        return
      }

      const errorCode = isHTTPError(error)
        ? error.response.status.toString()
        : String(error)
      showDialog({
        title: m.dialog_unknown_error_title(),
        description: m.dialog_unknown_error_content({ error_code: errorCode }),
        primaryButtonText: m.contact_cs(),
        primaryAction: () => {
          closeDialog()
          openIntercom({
            node: 'iap_unexpected_error',
            additional: { error_code: errorCode },
          })
        },
      })
    },
  }))
}
